import React, { useState, useEffect, useCallback } from 'react';
import PageWrapper from '../components/PageWrapper';
import {
  UserIcon,
  ShieldCheckIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import {
  getUsers,
  getUser,
  getRoles,
  assignRoleToUser,
  removeRoleFromUser,
} from '../services/adminService';

// ─── Role badge colours ────────────────────────────────────────────────────────
const ROLE_COLOURS = {
  agent: 'bg-blue-100 text-blue-800 border-blue-200',
  renter: 'bg-green-100 text-green-800 border-green-200',
  owner: 'bg-amber-100 text-amber-800 border-amber-200',
};

const PLATFORM_ROLE_COLOURS = {
  system_admin: 'bg-purple-100 text-purple-800 border-purple-200',
  account_manager: 'bg-indigo-100 text-indigo-800 border-indigo-200',
};

const roleBadgeClass = (roleName) =>
  `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
    ROLE_COLOURS[roleName] || 'bg-gray-100 text-gray-800 border-gray-200'
  }`;

// ─── Manage Roles Modal ────────────────────────────────────────────────────────
const ManageRolesModal = ({ user, allRoles, onClose, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(null); // role to confirm removal

  // roles the user does NOT yet have
  const assignableRoles = allRoles.filter(
    (r) => !user.roles.some((ur) => ur.id === r.id)
  );

  const handleAssign = async (roleId) => {
    setLoading(true);
    setError('');
    try {
      await assignRoleToUser(user.id, roleId);
      await onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (roleId) => {
    setLoading(true);
    setError('');
    setConfirmRemove(null);
    try {
      await removeRoleFromUser(user.id, roleId);
      await onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <UserIcon className="h-6 w-6 text-emerald-600 mr-2" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {user.given} {user.surname}
              </h2>
              <p className="text-sm text-gray-500">{user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Current roles */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Current Roles
            </h3>
            {user.roles.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No roles assigned</p>
            ) : (
              <div className="space-y-2">
                {user.roles.map((role) => (
                  <div
                    key={role.id}
                    className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                  >
                    <span className={roleBadgeClass(role.name)}>
                      <ShieldCheckIcon className="h-3 w-3 mr-1" />
                      {role.display_name || role.name}
                    </span>
                    {confirmRemove?.id === role.id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-600">Remove?</span>
                        <button
                          onClick={() => handleRemove(role.id)}
                          disabled={loading}
                          className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setConfirmRemove(null)}
                          className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmRemove(role)}
                        disabled={loading}
                        className="text-red-400 hover:text-red-600 disabled:opacity-50"
                        title="Remove role"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Assign new roles */}
          {assignableRoles.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                Assign Role
              </h3>
              <div className="space-y-2">
                {assignableRoles.map((role) => (
                  <div
                    key={role.id}
                    className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                  >
                    <span className="text-sm text-gray-700">
                      {role.display_name || role.name}
                    </span>
                    <button
                      onClick={() => handleAssign(role.id)}
                      disabled={loading}
                      className="flex items-center px-3 py-1 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <PlusIcon className="h-3 w-3 mr-1" />
                      Assign
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null); // full user with roles

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [usersData, rolesData] = await Promise.all([getUsers(), getRoles()]);
      setUsers(usersData || []);
      setAllRoles(rolesData || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reload a single user after role change and sync into list
  const handleRoleChanged = async () => {
    try {
      const updated = await getUser(selectedUser.id);
      setSelectedUser(updated);
      // Also refresh the full list to update badges
      const updatedList = await getUsers();
      setUsers(updatedList || []);
    } catch (e) {
      setError(e.message);
    }
  };

  const openModal = async (userListItem) => {
    try {
      const full = await getUser(userListItem.id);
      setSelectedUser(full);
    } catch (e) {
      setError(e.message);
    }
  };

  const filteredUsers = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      u.email.toLowerCase().includes(q) ||
      `${u.given} ${u.surname}`.toLowerCase().includes(q)
    );
  });

  return (
    <PageWrapper>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6">
              <div className="flex items-center">
                <UserIcon className="h-8 w-8 mr-3" />
                <div>
                  <h1 className="text-2xl font-bold">User Management</h1>
                  <p className="text-emerald-100 text-sm mt-1">
                    Manage user accounts and role assignments
                  </p>
                </div>
              </div>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative max-w-sm">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <p className="text-center text-gray-400 py-12">No users found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Name</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Email</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Roles</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Status</th>
                        <th className="py-3 px-4" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredUsers.map((u) => (
                        <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4 font-medium text-gray-900">
                            {u.given} {u.surname}
                          </td>
                          <td className="py-3 px-4 text-gray-600">{u.email}</td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1">
                              {u.platform_role && (
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                                    PLATFORM_ROLE_COLOURS[u.platform_role] || 'bg-gray-100 text-gray-800 border-gray-200'
                                  }`}
                                >
                                  <ShieldCheckIcon className="h-3 w-3 mr-1" />
                                  {u.platform_role.replace('_', ' ')}
                                </span>
                              )}
                              {(u.roles || []).map((r) => (
                                <span key={r.id} className={roleBadgeClass(r.name)}>
                                  {r.display_name || r.name}
                                </span>
                              ))}
                              {!u.platform_role && (u.roles || []).length === 0 && (
                                <span className="text-gray-400 text-xs italic">None</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                u.is_active
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {u.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button
                              onClick={() => openModal(u)}
                              className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                            >
                              Manage Roles
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Role management modal */}
      {selectedUser && (
        <ManageRolesModal
          user={selectedUser}
          allRoles={allRoles}
          onClose={() => setSelectedUser(null)}
          onChanged={handleRoleChanged}
        />
      )}
    </PageWrapper>
  );
};

export default UserManagement;

